import { useEffect, useState } from 'react'
import { api } from '../api/client.ts'
import { Avatar } from '../components/Avatar.tsx'
import { monthTitle } from '../lib/planner.ts'

/**
 * СЕМЕЙНЫЙ БЮДЖЕТ
 *
 * Открывается на текущем месяце и отвечает на один вопрос: куда ушли
 * деньги. Поэтому первое, что видно, — полосы по категориям, а список
 * записей лежит ниже.
 *
 * Запись расхода сделана в одну строку сверху: если внесение траты
 * занимает больше пяти секунд, его не станут делать, и через месяц
 * раздел будет пустым.
 */

type Category = {
  id: string
  name: string
  icon: string | null
  color: string | null
  spent: number
  limit: number | null
}

type Transaction = {
  id: string
  kind: string
  amount: number
  description: string | null
  occurredAt: string
  payer: { id: string; displayName: string; avatarUrl: string | null } | null
  category: { id: string; name: string; icon: string | null; color: string | null } | null
  mine: boolean
}

type Summary = {
  month: string
  spent: number
  earned: number
  categories: Category[]
  uncategorized: number
  byPerson: { id: string; displayName: string; avatarUrl: string | null; spent: number }[]
}

const money = (value: number): string =>
  value.toLocaleString('ru-RU', { maximumFractionDigits: 0 }) + ' ₽'

const monthParam = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`

export function BudgetPage() {
  const [month, setMonth] = useState(() => {
    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth(), 1)
  })

  const [summary, setSummary] = useState<Summary | null>(null)
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [categories, setCategories] = useState<{ id: string; name: string; icon: string | null }[]>([])

  const [amount, setAmount] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [description, setDescription] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [limitFor, setLimitFor] = useState<Category | null>(null)

  const load = () => {
    const from = new Date(month.getFullYear(), month.getMonth(), 1)
    const to = new Date(month.getFullYear(), month.getMonth() + 1, 0, 23, 59, 59)

    api<Summary>(`/api/budget/summary?month=${monthParam(month)}`)
      .then(setSummary)
      .catch((e) => setError(e instanceof Error ? e.message : 'Не удалось загрузить'))

    api<{ transactions: Transaction[] }>(
      `/api/budget/transactions?from=${from.toISOString()}&to=${to.toISOString()}`,
    )
      .then((data) => setTransactions(data.transactions))
      .catch(() => setTransactions([]))
  }

  useEffect(load, [month])

  useEffect(() => {
    api<{ categories: { id: string; name: string; icon: string | null }[] }>(
      '/api/budget/categories',
    )
      .then((data) => {
        setCategories(data.categories)
        setCategoryId((current) => current || data.categories[0]?.id || '')
      })
      .catch(() => setCategories([]))
  }, [])

  const add = async (kind: 'EXPENSE' | 'INCOME' = 'EXPENSE') => {
    const value = Number(amount.replace(',', '.').replace(/\s/g, ''))
    if (!Number.isFinite(value) || value <= 0) return

    setBusy(true)
    setError(null)
    try {
      await api('/api/budget/transactions', {
        method: 'POST',
        body: {
          kind,
          amount: value,
          categoryId: kind === 'EXPENSE' ? categoryId || null : null,
          description: description.trim() || null,
        },
      })
      setAmount('')
      setDescription('')
      load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не записалось')
    } finally {
      setBusy(false)
    }
  }

  const remove = async (transaction: Transaction) => {
    setTransactions((current) => current.filter((t) => t.id !== transaction.id))
    await api(`/api/budget/transactions/${transaction.id}`, { method: 'DELETE' }).catch(load)
    load()
  }

  const max = Math.max(...(summary?.categories.map((c) => c.spent) ?? [0]), 1)

  return (
    <div className="section">
      <header className="section-head">
        <button
          className="icon-btn"
          onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
          aria-label="Предыдущий месяц"
        >
          ‹
        </button>

        <div className="section-title">
          <h1>{monthTitle(month)}</h1>
          {summary && (
            <span className="section-sub">
              потрачено {money(summary.spent)}
              {summary.earned > 0 && ` · получено ${money(summary.earned)}`}
            </span>
          )}
        </div>

        <button
          className="icon-btn"
          onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
          aria-label="Следующий месяц"
        >
          ›
        </button>
      </header>

      {/* Запись траты — первое, что под рукой */}
      <div className="add-row">
        <input
          className="input"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && void add()}
          placeholder="Сколько"
          inputMode="decimal"
          style={{ maxWidth: 110 }}
        />

        <select
          className="input"
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
          style={{ maxWidth: 150 }}
        >
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.icon} {category.name}
            </option>
          ))}
        </select>

        <input
          className="input"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && void add()}
          placeholder="На что"
          maxLength={200}
        />

        <button className="btn-primary" onClick={() => void add()} disabled={busy || !amount.trim()}>
          Записать
        </button>
      </div>

      <div className="section-body scroll">
        {summary && summary.spent === 0 && transactions.length === 0 && (
          <p className="empty-hint">
            В этом месяце трат не записано. Введи сумму сверху — это занимает
            пять секунд, и в этом весь смысл.
          </p>
        )}

        {summary && summary.spent > 0 && (
          <>
            <h3 className="sheet-sub">Куда ушли</h3>
            {summary.categories
              .filter((category) => category.spent > 0 || category.limit !== null)
              .map((category) => {
                const over = category.limit !== null && category.spent > category.limit
                return (
                  <button
                    key={category.id}
                    className="budget-row"
                    onClick={() => setLimitFor(category)}
                  >
                    <span className="budget-name">
                      <span aria-hidden="true">{category.icon}</span> {category.name}
                    </span>

                    <span className="budget-bar">
                      <span
                        className={'budget-fill' + (over ? ' is-over' : '')}
                        style={{
                          width: `${Math.min((category.spent / max) * 100, 100)}%`,
                          background: over ? undefined : category.color ?? undefined,
                        }}
                      />
                      {/* Черта — это отметка на полосе, а не запрет */}
                      {category.limit !== null && category.limit > 0 && (
                        <span
                          className="budget-limit"
                          style={{ left: `${Math.min((category.limit / max) * 100, 100)}%` }}
                        />
                      )}
                    </span>

                    <span className={'budget-sum' + (over ? ' is-over' : '')}>
                      {money(category.spent)}
                      {category.limit !== null && (
                        <span className="budget-of"> из {money(category.limit)}</span>
                      )}
                    </span>
                  </button>
                )
              })}

            {summary.uncategorized > 0 && (
              <p className="sheet-note">Без категории: {money(summary.uncategorized)}</p>
            )}

            {summary.byPerson.length > 1 && (
              <>
                <h3 className="sheet-sub">Кто сколько</h3>
                <div className="metric-cards">
                  {summary.byPerson.map((person) => (
                    <div key={person.id} className="metric-card">
                      <span className="metric-name">
                        <Avatar
                          id={person.id}
                          name={person.displayName}
                          url={person.avatarUrl}
                          size={18}
                        />{' '}
                        {person.displayName}
                      </span>
                      <span className="metric-value">{money(person.spent)}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {transactions.length > 0 && (
          <>
            <h3 className="sheet-sub">Записи</h3>
            {transactions.map((transaction) => (
              <div key={transaction.id} className="item-row">
                <span className="item-main" style={{ cursor: 'default' }}>
                  <span className="item-title">
                    {transaction.category?.icon ?? (transaction.kind === 'INCOME' ? '💰' : '•')}{' '}
                    {transaction.description || transaction.category?.name || 'Без описания'}
                  </span>
                  <span className="item-marks">
                    <span className={transaction.kind === 'INCOME' ? 'budget-income' : ''}>
                      {transaction.kind === 'INCOME' ? '+' : '−'}
                      {money(transaction.amount)}
                    </span>
                    <span>
                      {new Date(transaction.occurredAt).toLocaleDateString('ru-RU', {
                        day: 'numeric',
                        month: 'short',
                      })}
                    </span>
                    {transaction.payer && (
                      <Avatar
                        id={transaction.payer.id}
                        name={transaction.payer.displayName}
                        url={transaction.payer.avatarUrl}
                        size={20}
                      />
                    )}
                  </span>
                </span>

                {transaction.mine && (
                  <button
                    className="item-remove"
                    onClick={() => void remove(transaction)}
                    aria-label="Удалить запись"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </>
        )}

        {error && <div className="sheet-error">{error}</div>}
      </div>

      {limitFor && (
        <LimitSheet
          category={limitFor}
          month={monthParam(month)}
          onClose={() => setLimitFor(null)}
          onSaved={() => {
            setLimitFor(null)
            load()
          }}
        />
      )}
    </div>
  )
}

function LimitSheet({
  category,
  month,
  onClose,
  onSaved,
}: {
  category: Category
  month: string
  onClose: () => void
  onSaved: () => void
}) {
  const [amount, setAmount] = useState(category.limit?.toString() ?? '')
  const [busy, setBusy] = useState(false)

  const save = async (value: number | null) => {
    setBusy(true)
    try {
      await api('/api/budget/limits', {
        method: 'PUT',
        body: { categoryId: category.id, month, amount: value },
      })
      onSaved()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="sheet" onClick={onClose}>
      <div className="sheet-body" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-head">
          <h2>
            {category.icon} {category.name}
          </h2>
          <button className="icon-btn" onClick={onClose} aria-label="Закрыть">
            ✕
          </button>
        </div>

        <p className="sheet-text">В этом месяце потрачено {money(category.spent)}.</p>

        <label className="field">
          <span className="field-label">Черта на месяц</span>
          <input
            className="input"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="Например, 20 000"
            inputMode="decimal"
            autoFocus
          />
          <span className="field-hint">
            Это отметка, а не запрет: приложение не помешает потратить больше,
            но покажет, что черта пройдена.
          </span>
        </label>

        <div className="sheet-actions">
          <button
            className="btn-primary"
            disabled={busy || !amount.trim()}
            onClick={() => void save(Number(amount.replace(',', '.').replace(/\s/g, '')))}
          >
            Поставить
          </button>
          {category.limit !== null && (
            <button className="btn-quiet" disabled={busy} onClick={() => void save(null)}>
              Убрать черту
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
